import React, { useState } from 'react';

interface JobContent {
  description: string;
  requirements: string[];
  responsibilities: string[];
}

interface AIJobDescriptionGeneratorProps {
  jobTitle?: string;
  experienceLevel?: string;
  keySkills?: string[];
  onContentGenerated?: (content: JobContent) => void;
}

export const AIJobDescriptionGenerator: React.FC<AIJobDescriptionGeneratorProps> = ({
  jobTitle: initialJobTitle = '',
  experienceLevel: initialExperienceLevel = '',
  keySkills: initialKeySkills = [],
  onContentGenerated,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!initialJobTitle.trim()) {
      setError('Please provide a job title first');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const { unifiedApiService } = await import('../../services/unified');
      const response = await unifiedApiService.ai.generateJobDescription({
        job_title: initialJobTitle,
        requirements: initialKeySkills.length > 0 ? initialKeySkills : undefined,
        experience_level: initialExperienceLevel || undefined,
      });

      // Parse the response content - it should be a string
      let content = '';
      if (typeof response.content === 'string') {
        content = response.content;
      } else if (Array.isArray(response.content)) {
        content = response.content.join('\n\n');
      }
      
      // For now, just use the description as-is
      // The backend returns a formatted description, not structured data
      const requirements: string[] = [];
      const responsibilities: string[] = [];

      // Immediately populate the form fields
      if (onContentGenerated && content) {
        onContentGenerated({
          description: content,
          requirements: requirements,
          responsibilities: responsibilities,
        });
      }
      
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to generate job description');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="inline-block">
      <button
        onClick={handleGenerate}
        disabled={isGenerating || !initialJobTitle}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isGenerating ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Generating...
          </>
        ) : (
          <>
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
            AI Generate
          </>
        )}
      </button>
      
      {error && (
        <div className="mt-2 text-sm text-red-600">
          {error}
        </div>
      )}
    </div>
  );
};
